import { useDebounce, useGetAddressV2 } from "@api";
import type { IAddress } from "@bbj-types/address";
import type { AutocompleteInputChangeReason } from "@mui/material";
import { useCallback, useEffect, useState } from "react";

interface IAddressBackendProperties {
  label: string;
  city: string;
  citycode: string;
  postcode: string;
  street: string;
  housenumber: string;
  complementaryAddress?: string;
}

interface IAddressBackendGeometry {
  coordinates: [number, number];
}
export interface IAddressBackend {
  properties: IAddressBackendProperties;
  geometry: IAddressBackendGeometry;
}

const TIMEOUT = 1000;

export const useAddress = (
  propsValue: IAddress | null | undefined,
  onInputChange?: (
    event: React.SyntheticEvent<Element, Event>,
    newInputValue: string,
    reason: AutocompleteInputChangeReason
  ) => void,
  disableGoogle?: boolean
) => {
  const [options, setOptions] = useState<IAddress[]>([]);
  const [{ loading: apiHookLoading, data: apiHookResult }, getApiHookResult] = useGetAddressV2(false);

  useEffect(() => {
    if (Array.isArray(apiHookResult)) {
      setOptions(apiHookResult);
    }
  }, [apiHookResult]);

  const debounce = useDebounce(
    (newInputValue: string) => {
      if (newInputValue === "" || newInputValue.length < 3) {
        setOptions([]);
      } else if (getApiHookResult) {
        void getApiHookResult(newInputValue, undefined, disableGoogle);
      }
    },
    TIMEOUT,
    [getApiHookResult]
  );

  const handleInputChange = useCallback(
    (event: React.SyntheticEvent<Element, Event>, newInputValue: string, reason: AutocompleteInputChangeReason) => {
      if (propsValue?.formatted === newInputValue) {
        return;
      }
      if (typeof onInputChange === "function") {
        onInputChange(event, newInputValue, reason);
      }
      debounce(newInputValue);
    },
    [debounce, onInputChange, propsValue?.formatted]
  );

  return {
    options,
    apiHookLoading,
    handleInputChange,
  };
};
